import { defineCopy } from '@floorfile/i18n'

/**
 * The app-level shared components (localization plan Phase 5, chunk 5c-1).
 *
 * `apps/web/src/components` is the layer between `@floorfile/ui` (the design
 * system, which owns its own catalogue and knows nothing about this product)
 * and a feature slice (which owns its domain words). What lives here is
 * product-shaped but domain-free: the generic edit dialog fourteen registers
 * open, the searchable entity picker every filter bar uses, the user picker,
 * the crash boundary under every route, the chart boundary under every chart,
 * and the "a deploy landed under your feet" banner.
 *
 * ## Why this chunk jumped the queue
 *
 * `RecordEditDialog`'s **Cancel** and **Save changes** were the only English
 * left on three otherwise-fully-French edit dialogs — `deviations-oos`,
 * `change-control` and `forms` all migrated in batch 5b and all open this
 * component. A shared primitive is the one place where a missed string is
 * visible on every screen at once, which is also why it is the easiest kind to
 * keep postponing: it belongs to no slice, so no slice's chunk ever includes
 * it.
 *
 * ## Two class components, and how they reach the catalogue
 *
 * `FeatureErrorBoundary` and `ChartFrame`'s inner boundary are React CLASS
 * components — an error boundary still has no hook equivalent — so neither can
 * call `useCopy`. `getCopy(…, getActiveLocale())` would compile, but a class
 * does not subscribe to the translation registry, so the fallback would keep
 * rendering whatever locale was active when it mounted. Both render their
 * message through a small FUNCTION component instead
 * (`FeatureErrorFallback`, `ChartUnavailable`), which is a hook boundary again.
 * The class keeps the `componentDidCatch` behaviour; only the words move.
 *
 * ## English changes in this chunk, and why each is allowed
 *
 * 1. **`PagedEntitySelector`'s off-page selection count** was
 *    `{n} selected item(s) outside this result page` — a hand-rolled English
 *    plural (defect class 6). `(s)` is not a plural, it is an apology for not
 *    having one, and it is unwritable in French where the article inflects too.
 *    It is now a `{ one, other }` record: `1 selected item outside this result
 *    page` / `3 selected items outside this result page`.
 * 2. **`ChartFrame`'s loading label** was `` `Drawing ${label}…` `` — a
 *    template literal gluing a translated fragment onto an English word in an
 *    English position (defect class 5). It is one message with a `{label}`
 *    placeholder now. Rendered English is unchanged.
 *
 * Nothing else moves. `apps/e2e` has no text locator on any string in this
 * file — checked before writing it, because these words sit under every
 * register in the product and a byte change here would break locators in
 * modules this chunk never touched.
 *
 * ## What deliberately stayed a literal
 *
 * `RecordEditDialog`'s empty-select option renders `—` and its required marker
 * renders `*`. Both are punctuation rather than words: there is nothing for a
 * translator to decide, and putting them in a catalogue would invite someone to
 * "translate" a typographic convention. `PagedEntitySelector`'s ` *` suffix is
 * the same.
 */
export const componentsCopy = defineCopy('components', {
  /** The drill-through scope strip (FF-AUDIT-004/032): what a register was
   * opened WITH, each part removable. */
  /**
   * The product’s answer to `window.confirm` and `window.prompt`.
   *
   * Fifteen call sites across twelve features asked the BROWSER to confirm
   * a destructive action or collect a reason. Chrome writes its buttons in
   * the BROWSER’s language, not the app’s — so a French user cancelling a
   * check saw "OK / Cancel" in English, and a reason typed into a system
   * prompt still became part of a permanent quality record. These three
   * words are the defaults; a caller with a better verb passes its own.
   */
  ask: {
    confirm: 'Confirm',
    cancel: 'Cancel',
    required: 'Please say why — this goes on the record.',
  },
  drillScope: {
    heading: 'Showing only',
    remove: 'Remove {label} filter',
    clearAll: 'Show everything',
  },
  /** The generic "backend ready, UI missing" edit dialog (audit H-05). */
  recordEdit: {
    cancel: 'Cancel',
    /** The create-dialog run counter — see RecordEditDialog's addAnotherLabel. */
    addedSoFar: '{count} added.',
    done: 'Done',
    /** The default submit label. Callers override it with a domain verb
     * ("Release hold", "Publish"), so this is the neutral fallback only. */
    save: 'Save changes',
    saving: 'Saving…',
    /** Shown only when the thrown error carries no message of its own — a real
     * API failure arrives already translated through `lib/errors`. */
    saveFailed: 'Could not save the changes.',
    /**
     * FF-AUDIT-005. A number outside the bounds its `FieldSpec` declares, said
     * under the field before the request — and the floor for a server issue
     * on a field the caller has no specific wording for: the field is still
     * named, instead of a dialog-level "some details are not valid".
     */
    numberRange: 'Must be between {min} and {max}.',
    numberMin: 'Must be at least {min}.',
    numberMax: 'Must be at most {max}.',
    numberInvalid: 'Enter a number.',
    fieldInvalid: 'This value is not valid.',
  },

  /**
   * `EntityPicker` (Fiix audit §8.4) — the typed picker that replaced the
   * native `<select>`s holding every asset. Placeholders per entity type;
   * `recent` prefixes the detail line of a recently picked option.
   */
  entityPicker: {
    recent: 'Recent',
    loadFailed: 'Could not load the list.',
    retry: 'Retry',
    placeholder: {
      equipment: 'Search equipment by name, tag or serial…',
      part: 'Search parts by number or name…',
      location: 'Search locations…',
      vendor: 'Search vendors…',
      worker: 'Search workers…',
      facility: 'Search facilities…',
    },
  },

  /** F13: a record's photo thumb is a button that opens the photo full size. */
  photo: {
    view: 'View photo of {name}',
    /** When the thumb has no record name beside it. */
    viewUnnamed: 'View photo',
  },

  /** `ImageField` (Fiix audit Phase 2) — the photo on an asset or part. */
  imageField: {
    add: 'Add photo',
    replace: 'Replace photo',
    remove: 'Remove photo',
  },

  /** `FilterBar` (Fiix audit §8.3) — the one register filter bar. */
  filterBar: {
    search: 'Search',
    quickFilters: 'Quick filters',
    filters: 'Filters',
    activeFilters: 'Applied filters',
    /** FF-AUDIT-20260921-210633-034: these filters came back from the last visit. */
    restored: 'Filters from your last visit',
    /** The accessible name of a chip's × button; `{label}` is the chip text. */
    remove: 'Remove filter {label}',
    /** The empty option of every select in the Filters panel. */
    any: 'Any',
    done: 'Done',
    views: 'Views',
    loading: 'Loading…',
    noViews: 'No saved views yet. Set your filters, then save them as a view.',
    personal: 'My views',
    company: 'Company views',
    saveCurrent: 'Save current filters as a view…',
    viewName: 'View name',
    shareWithCompany: 'Share with everyone in the company',
    cancel: 'Cancel',
    save: 'Save view',
    saved: 'View “{name}” saved.',
    deleteView: 'Delete view {name}',
    loadFailed: 'Could not load saved views.',
    retry: 'Retry',
  },

  /** The shared searchable picker behind every entity filter and assignee field. */
  selector: {
    clear: 'Clear',
    /** The accessible name of the inline clear button inside a single-select
     * token. `{label}` is the caller's already-translated field label. */
    clearNamed: 'Clear {label}',
    /**
     * Multi-select only: some chosen rows are not on the page being shown.
     *
     * French treats zero as singular, and the count is never zero here (the
     * line only renders when at least one selection is off-page) — but the
     * record shape is what lets a locale say so, and `one`/`other` is what the
     * selector keys on.
     */
    offPage: {
      one: '{count} selected item outside this result page',
      other: '{count} selected items outside this result page',
    },
    noResults: 'No matching results.',
    loading: 'Loading…',
    loadMore: 'Load more results',
  },

  /** `UserPicker` / `UserMultiPicker` — their own field labels and placeholder. */
  userPicker: {
    /** The single-select default label. A caller that knows the role the
     * person plays ("Assignee", "Approver") passes its own. */
    user: 'User',
    users: 'Users',
    searchPlaceholder: 'Search users…',
  },

  /** The boundary wrapping the shell's `<Outlet/>` (audit RUN-12). */
  featureError: {
    title: 'This screen hit a problem',
    body: 'The rest of the app is still working — use the menu to go somewhere else, or reload to try this screen again.',
    retry: 'Try again',
    /** The screen was never downloaded and there is no connection to fetch it
     * — a known limitation of the offline-first precache allow-list, not a
     * fault. No retry: it cannot succeed until there is a network. */
    offlineTitle: 'This screen is not available offline yet',
    offlineBody:
      'It has not been downloaded to this device. Open it once with a connection and it will work offline from then on.',
  },

  /** The advisory banner shown when a new build claims an open tab. */
  newVersion: {
    message: 'A new version of FloorFile is available. Reload to finish updating.',
    reload: 'Reload',
    dismiss: 'Not now',
  },

  /**
   * The ONLINE-ONLY gate (`useOnlineOnlyGate` / `OnlineOnlyNotice` in
   * `online-only.tsx`) — the offline-resilience doc's §2 tier for admin-type
   * writes (documents, HR records, training, workforce, change control,
   * supplier quality). These actions read/affect whole-company state and are
   * never queued; offline, the control says so instead of failing on submit
   * or hanging on a dead request.
   */
  onlineOnly: {
    /** The disabled control's `title` — short, for a pointer-hover tooltip. */
    reason: 'Needs a connection',
    /** The one-line notice shown beside the control. What was typed stays in
     * the form; nothing is sent until there is a connection again. */
    notice: 'This needs a connection. What you entered is kept — save it when you are back online.',
  },

  /** The lazy-chart boundary: charts are runtime-cached, not precached. */
  chart: {
    /** Says what happened AND what to do. "Chart unavailable" alone reads as a
     * broken feature; the reason is the difference between a bug and a known
     * limitation. */
    unavailable:
      'This chart needs a connection the first time it is shown. Reconnect and reload to see it — the figures above are unaffected.',
    /** `{label}` is what the chart shows, supplied by the calling slice. */
    drawingNamed: 'Drawing {label}…',
    drawing: 'Drawing chart…',
  },
})

export type ComponentsCopy = typeof componentsCopy.en
